import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { sendEmail } from '../../src/services/emailService';

export default function QuoteForm() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm({
        mode: 'all',
        defaultValues: {
            name: '',
            company: '',
            email: '',
            phone: '',
            message: '',
        },
    });

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setSuccess(null);
        try {
            await sendEmail({
                recipientEmail: process.env.REACT_APP_RECIPIENT_EMAIL,
                senderEmail: process.env.REACT_APP_SENDER_EMAIL,
                subject: `Quote Request from ${data.name}`,
                content: `
            <b>Name:</b> ${data.name}<br/>
            <b>Company:</b> ${data.company}<br/>
            <b>Email:</b> ${data.email}<br/>
            <b>Phone:</b> ${data.phone}<br/>
            <b>Message:</b> ${data.message}
        `,
                senderName: data.name,
                replyTo: data.email,
            });
            setSuccess('Your request has been sent! We will contact you soon.');
            reset();
        } catch (error) {
            setSuccess('There was an error sending your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-logo-first/10 to-logo-third/10 py-12 px-4">
            <div className="max-w-lg w-full bg-white rounded-xl shadow-2xl p-10">
                <h2 className="text-3xl font-bold text-logo-first mb-2 text-center">Request a Quote</h2>
                <p className="text-med-grey mb-8 text-center">Fill out the form below and our team will get back to you promptly.</p>
                <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        <label className="block text-md font-medium text-deep-grey mb-1">
                            Name <span className="text-logo-first">*</span>
                        </label>
                        <input
                            className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-logo-first ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                            type="text"
                            {...register('name', { required: 'Name is required' })}
                            placeholder="Your name"
                            disabled={isSubmitting}
                        />
                        {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>}
                    </div>
                    <div>
                        <label className="block text-md font-medium text-deep-grey mb-1">
                            Company Name <span className="text-logo-first">*</span>
                        </label>
                        <input
                            className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-logo-first ${errors.company ? 'border-red-400' : 'border-gray-300'}`}
                            type="text"
                            {...register('company', { required: 'Company name is required' })}
                            placeholder="Your company name"
                            disabled={isSubmitting}
                        />
                        {errors.company && <p className="text-sm text-red-500 mt-1">{errors.company.message}</p>}
                    </div>
                    <div>
                        <label className="block text-md font-medium text-deep-grey mb-1">
                            Email <span className="text-logo-first">*</span>
                        </label>
                        <input
                            className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-logo-first ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                            type="email"
                            {...register('email', {
                                required: 'Email is required',
                                pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' }
                            })}
                            placeholder="Your email address"
                            disabled={isSubmitting}
                        />
                        {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
                    </div>
                    <div>
                        <label className="block text-md font-medium text-deep-grey mb-1">
                            Phone <span className="text-logo-first">*</span>
                        </label>
                        <input
                            className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-logo-first ${errors.phone ? 'border-red-400' : 'border-gray-300'}`}
                            type="tel"
                            {...register('phone', { required: 'Phone number is required' })}
                            placeholder="Your phone number"
                            disabled={isSubmitting}
                        />
                        {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>}
                    </div>
                    <div>
                        <label className="block text-md font-medium text-deep-grey mb-1">
                            Message <span className="text-logo-first">(Optional)</span>
                        </label>
                        <textarea
                            className={`form-input w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-logo-first ${errors.message ? 'border-red-400' : 'border-gray-300'}`}
                            rows={4}
                            {...register('message')}
                            placeholder="Tell us about your needs"
                            disabled={isSubmitting}
                        />
                        {errors.message && <p className="text-sm text-red-500 mt-1">{errors.message.message}</p>}
                    </div>
                    <button
                        type="submit"
                        className="w-full py-3 rounded-lg bg-logo-first text-white font-semibold text-lg hover:bg-logo-third transition disabled:opacity-60"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending...' : 'Request Quote'}
                    </button>
                    {success && (
                        <div className={`mt-4 text-center text-lg ${success.startsWith('Your request') ? 'text-green-600' : 'text-red-600'}`}>
                            {success}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
